<div class="flex flex-col w-full overflow-hidden" style="height: min(84rem, 95vh)">
  @if (store.isLoading()) {
    <div class="flex items-center justify-center flex-grow w-full p-40" @fadeIn>
      <div class="w-full px-30 py-25 border rounded border-gray-lighter bg-gray-lightest" style="max-width: 42rem">
        <h2 class="text-lg font-bold text-gray-darkest">Gathering account details</h2>
        <!-- Confirms the advisor is waiting on the account they picked, in the same three
             parts and the same order as the header they will see once it loads: who it is
             for, where it is held, and the number to match against another system.
             
             Read from the `account` INPUT, not `store.data()` — that is what is still
             loading. AccountModel has no clientName field; it is derived from `clients`, so
             this shares `deriveClientName` with the service rather than reproducing it. An
             earlier version used `title` alone, which is only the FALLBACK, so the name was
             missing on every account that actually had clients attached. Still guarded — it
             is empty on an account with neither. -->
        @if (account(); as acct) {
          <div class="flex flex-wrap items-baseline mt-2 text-sm gap-x-8 gap-y-2">
            @if (loadingClientName(); as client) {
              <span class="font-semibold text-gray-dark">{{ client }}</span>
              <span class="text-gray-light" aria-hidden="true">|</span>
            }
            <span class="text-gray">{{ acct.custodian }}</span>
            <span class="text-gray-light" aria-hidden="true">|</span>
            <span class="text-gray" style="font-variant-numeric: tabular-nums"
              [attr.aria-label]="'Account number ' + acct.accountNumber">{{ acct.accountNumber }}</span>
          </div>
        }

        <!-- Determinate, because the stages are real request boundaries — we know the
             fraction rather than having to animate a guess. -->
        <div class="w-full h-4 mt-20 overflow-hidden rounded-full bg-gray-lighter">
          <div class="h-full transition-all duration-300 rounded-full bg-mint-dark"
            [style.width.%]="loadPercent()"></div>
        </div>

        <div class="mt-20">
          @for (stage of loadStages; track stage.id; let last = $last) {
            <div class="flex gap-x-15">
              <!-- Marker column: a fixed-width chip plus the connector to the next stage,
                   so the three read as one sequence rather than three loose lines. -->
              <div class="flex flex-col items-center flex-shrink-0" style="width: 2.4rem">
                @switch (loadStageState(stage.id)) {
                  @case ('done') {
                    <div class="flex items-center justify-center rounded-full bg-mint-lighter" style="width: 2.4rem; height: 2.4rem">
                      <i class="material-icons text-mint-darkest" style="font-size: 1.6rem">check</i>
                    </div>
                  }
                  @case ('active') {
                    <div class="flex items-center justify-center bg-white border-2 rounded-full border-mint-light" style="width: 2.4rem; height: 2.4rem">
                      <div class="loader-xs is-mint"></div>
                    </div>
                  }
                  @default {
                    <div class="flex items-center justify-center border rounded-full bg-gray-lightest border-gray-lighter" style="width: 2.4rem; height: 2.4rem">
                      <div class="rounded-full bg-gray-light" style="width: 0.6rem; height: 0.6rem"></div>
                    </div>
                  }
                }
                @if (!last) {
                  <div class="flex-grow my-2" style="width: 2px"
                    [class.bg-mint-light]="loadStageState(stage.id) === 'done'"
                    [class.bg-gray-lighter]="loadStageState(stage.id) !== 'done'"></div>
                }
              </div>
              <div class="pb-20" [class.pb-0]="last">
                <span class="text-sm"
                  [class.font-semibold]="loadStageState(stage.id) === 'active'"
                  [class.text-gray-darkest]="loadStageState(stage.id) === 'active'"
                  [class.text-gray-dark]="loadStageState(stage.id) === 'done'"
                  [class.text-gray-light]="loadStageState(stage.id) === 'pending'">
                  {{ stage.label }}
                </span>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  } @else if (store.loadError()) {
    <section class="flex flex-col items-center justify-center flex-grow gap-y-15 p-40 text-center" @fadeIn>
      <i class="material-icons text-red" style="font-size: 4.8rem">error_outline</i>
      <h2 class="text-xl font-bold text-gray-darkest">Unable to Load</h2>
      <p class="max-w-md text-sm text-gray-dark">
        {{ store.loadErrorMessage() }}
      </p>
      <button type="button" class="button is-mint is-lg" (click)="dismiss()">Close</button>
    </section>
  } @else if (store.isComplete()) {
    <section class="flex flex-col items-center justify-center flex-grow gap-y-15 p-40 text-center" @fadeIn>
      <i class="material-icons text-green-dark" style="font-size: 4.8rem">check_circle</i>
      <h2 class="text-xl font-bold text-gray-darkest">Request Submitted</h2>
      <p class="max-w-md text-sm text-gray-dark">
        The {{ title() }} request has been submitted successfully.
      </p>
      <button type="button" class="button is-mint is-lg" (click)="closeRequest()">Close</button>
    </section>
  } @else {
    <!-- Header -->
    <!-- Vertical padding trimmed from 2rem to 1rem a side. The title and the account line
         were sitting in 3rem of space top and bottom -- 2rem from the shared dialog rule plus
         1rem of margin on the block below -- in a modal whose height is fixed, so all of it
         came off the only region that scrolls.
         
         An INLINE style, not a utility class: the shared rule is
         `mat-dialog-container.mat-mdc-dialog-container header` (styles.theme.scss:392), whose
         specificity of one class and two elements beats any single Tailwind class here. Inline
         keeps the override local to this wizard rather than editing a rule every dialog in the
         portal shares. -->
    <header class="flex-shrink-0" style="padding-top: 1rem; padding-bottom: 1rem">
      <div class="flex flex-col mx-10 my-0 mr-20">
        <h2 class="text-xl font-bold text-gray-darkest">{{ title() }} Request</h2>
        <!-- Three values of three different kinds, so they are no longer typeset as one
             sentence of dash-separated text.
             
             The client is who the request is FOR, so it leads and carries the weight. The
             account number is the thing a reader matches against another system, so it is
             tabular-nums -- proportional digits in a long number are hard to compare
             character by character. The custodian is context and recedes.
             
             No "#" prefix: it read as clutter beside two plain words. The aria-label is now
             the only thing marking the number as an identifier rather than an amount, so keep
             it if this line is touched again.
             
             ⚠️ Since the Account card came off Review & Submit, this is the ONLY place these
             three appear in the whole wizard. Do not thin it out further. -->
        @if (store.data(); as data) {
          <div class="flex flex-wrap items-baseline text-base gap-x-8 gap-y-2">
            <span class="font-semibold text-gray-dark">{{ data.account.clientName }}</span>
            <span class="text-gray-light" aria-hidden="true">|</span>
            <span class="text-gray">{{ data.account.custodian }}</span>
            <span class="text-gray-light" aria-hidden="true">|</span>
            <span class="text-gray" style="font-variant-numeric: tabular-nums"
              [attr.aria-label]="'Account number ' + data.account.accountNumber">{{ data.account.accountNumber }}</span>
          </div>
        }
      </div>
      <button mat-button type="button" class="m-10 ml-auto icon-button is-mint is-inverted"
        (click)="closeRequest()" aria-label="Close">
        <i class="material-icons">close</i>
      </button>
    </header>

    <app-rav2-step-indicator class="flex-shrink-0" [steps]="stepLabels()" [current]="store.currentStep()"
      [navigableSteps]="store.navigableSteps()" [lockedSteps]="store.smaLockedSteps()"
      (stepSelected)="store.goToStep($event)" (lockedStepSelected)="onLockedStep()" />

    <!-- The master equation, once, for the whole wizard.

         Deliberately NOT inside the @switch below: a single persistent instance is the entire
         point, and re-rendering it per step would also make it take the shell's @fadeIn on
         every navigation and compete with the step body's own 500ms fade.

         ⚠️ Hidden on Review & Submit, and the @if is NOT the re-rendering the note above warns
         against. That warning is about a case per step, which would tear the strip down on every
         navigation; this condition is false on exactly one step, so the instance persists across
         all the others and is rebuilt at most once. The strip is derived entirely from the
         store and holds no state of its own, so rebuilding it costs nothing.

         It goes because Review & Submit already IS the equation, itemised: every figure the
         strip carries appears there in full, against the rows that produced it. Two summaries of
         the same request on one screen invites reconciling them instead of reading the request --
         and the strip is a running total for a form still being filled in, which this step is not.

         Its own px-20 to match the indicator's gutters -- it sits outside the step body's
         dialog-content padding box -- and border-b only, never border-t, or the hairline
         doubles against the indicator above it. -->
    <!-- ⚠️ Now shown for Contribution and Distribution too, reversing an earlier decision. Those
         types were given per-step totals rows instead, on the reasoning that the strip's terms
         describe a reallocation's equation — true of the TERMS, but the mechanism was exactly
         what they needed. A distribution is sourced from models, custodial cash and sells across
         two separate steps, so no per-step total could answer "where am I overall", and the
         advisor had to hold the running figure in their head. The terms are type-aware now
         (see `contextTerms` / `moveMoneyTerms`).

         ⚠️ SHOWN ON FUNDING NOW, in a reduced form (AndyP, 2026-09-22). It was hidden there
         outright, and the reason still holds for most of it: the amount is being set on that
         step, so a remainder box would read the full amount back as though something were
         outstanding, and the bar has no basis to partition yet. But the account TOTAL is true
         before anything is decided, and it is what an advisor sizing a distribution wants to see.
         The strip suppresses its own bar and remainder there — see `isContextOnly`.

         Review stays excluded because it IS the itemised equation. So Close Account shows only
         the total, its two steps being funding and review. -->
    @if (store.currentStepId() !== 'review') {
      <app-rav2-calculation-strip class="flex-shrink-0" />
    }

    <!-- Step body -->
    <section class="flex-grow min-h-0 m-10 overflow-y-auto dialog-content">
      <!-- ⚠️ THE STEP TITLE, rendered here for every step rather than by each step for itself.
           See `currentStepLabel` for why. Inside the scrolling section on purpose: it belongs to
           the body it names, and pinning it outside would hold a heading above content that has
           scrolled past it.

           ⚠️ EXCEPT FUNDING, which supplies its own (AndyP, 2026-09-22). That step is two columns
           -- Amount and Delivery -- and a title above them both left the right column captioned
           and the left one apparently not, so the pair did not read as a pair. The step now heads
           its left column itself, and a title here would sit above a heading pair that already
           says what each half is for.

           🟢 SETTLED, not a loose end (AndyP, 2026-10-02), checked against the rendered step.
           Delivery STAYS on this step and is deliberately NOT named in the step title or in the
           stepper chip, which reads 'Distribution Amount'. Moving it to a step of its own was
           considered and rejected: the two decisions are independent, neither constrains the
           other, and splitting them would add a step to make room for a word.

           ⚠️ So the two column <h3>s are the ONLY thing naming each half. Neither is redundant --
           see `heading()` in the funding step, which says the same thing from the other side. The
           earlier long title ('Distribution Funding and Delivery') named the right column on the
           left column's behalf; shortening it removed that backstop on purpose. -->
      <!-- The stepper above carries the type ('Distribution Amount' / 'Contribution Amount'). -->
      @if (store.currentStepId() !== 'funding') {
        <h3 class="mb-10 text-lg font-bold text-gray-darkest">{{ currentStepLabel() }}</h3>
      }
      <!-- Switched on the step id, not its position: the optional Attach Files step
           shifts everything after Allocate to Models by one when it's enabled. -->
      @switch (store.currentStepId()) {
        @case ('funding') { <app-rav2-funding-step @fadeIn /> }
        @case ('sleeveAmounts') { <app-rav2-sleeve-amount-step @fadeIn /> }
        @case ('positions') { <app-rav2-review-positions-step @fadeIn /> }
        @case ('buys') { <app-rav2-buy-securities-step @fadeIn /> }
        @case ('doNotTrade') { <app-rav2-do-not-trade-step @fadeIn /> }
        @case ('protectedCash') { <app-rav2-protected-cash-step @fadeIn /> }
        @case ('allocation') { <app-rav2-model-allocation-step @fadeIn /> }
        @case ('files') { <app-rav2-attach-files-step @fadeIn /> }
        @case ('review') { <app-rav2-review-submit-step @fadeIn /> }
      }
    </section>

    <!-- Footer -->
    <!-- Same trim as the header, and for the same reason: 2rem of shared dialog padding plus
         1rem of margin on every control. Inline because the shared rule
         (`mat-dialog-container.mat-mdc-dialog-container footer`, styles.theme.scss:395) outranks
         any single utility class used here. -->
    <footer class="flex-shrink-0" style="padding-top: 1rem; padding-bottom: 1rem">
      @if (store.currentStep() > 1) {
        <!-- Back is disabled rather than hidden while something holds the advisor on a step:
             a control that vanishes reads as a bug, and the panel on the step is already
             saying what is being asked for.

             Three things can hold it. The Do Not Trade attestation is temporary -- confirm, or
             unmark the positions. A confirmed SMA is not: the earlier steps were cleared by
             the switch, so the only ways on are Submit and the X. An SMA the account already
             holds sits in between -- removing the model on this step opens the earlier ones.

             ⚠️ Each one needs its own title. A greyed Back with nothing to hover is the
             failure this wizard keeps finding: the advisor cannot tell a lock from a bug. -->
        <button type="button" class="mx-10 my-0 button is-mint is-inverted is-lg is-outlined"
          [disabled]="store.isSubmitting() || !store.canGoBack()"
          [title]="store.smaCommitted()
            ? 'This request has been switched to a Separately Managed Account — submit it or close to start over'
            : store.smaLockActive()
              ? 'The earlier steps are unavailable while this account holds a separately managed account — remove the model to open them'
              : store.dntConfirmationRequired() ? 'Confirm the Do Not Trade selections first' : ''"
          (click)="store.goBack()">Back</button>
      }
      <!-- 🔴 ONE CENTRED PANEL FOR EVERY REASON NEXT IS DEAD (AndyP, 2026-10-29).

           There were six separate spans pressed against the button, right-aligned at `max-w-md`,
           which wrapped a three-clause sentence into four ragged lines in the corner of the
           screen -- grey text beside a greyed button, in the one place the eye is least likely to
           rest. The advisor is looking at the control they just failed to press, so that is where
           the explanation sits now.

           ⚠️ RED, AND ALL OF THEM THE SAME. The old split -- grey for the step reasons, red for
           funding and submit -- read as accidental, and it was: the funding note justified its red
           by saying it was "the sole statement of why Next is dead". Every one of these is that,
           now they share one home. Red is this wizard's blocker colour, and these are the same
           classes the over-committed banner uses on the steps, so a reason in the footer and a
           reason on a step read as one kind of thing.

           ⚠️ ONE EXPRESSION, not six blocks, because they are mutually exclusive by step and six
           copies of the panel is six places to forget. Submit blockers and the Do Not Trade
           attestation come FIRST: both can coexist with a step reason, and both are the more
           specific statement.

           ⚠️ `max-width` INLINE rather than a utility. The FIG config does not carry every
           Tailwind width, and a class that generates no CSS fails silently -- the trap already
           recorded against `w-250` and `pl-16` elsewhere in this feature. -->
      @let footerBlocker =
        store.currentStep() === store.totalSteps && store.submitBlockers().length
            ? 'Resolve the ' + (store.submitBlockers().length === 1 ? 'item' : 'items') + ' above to submit'
          : store.dntConfirmationRequired()
            ? 'Confirm the Do Not Trade selections to continue'
          : store.currentStepId() === 'allocation' ? store.allocationStepBlockedReason()
          : store.currentStepId() === 'positions' ? store.positionsStepBlockedReason()
          : store.currentStepId() === 'funding' ? store.fundingStepBlockedReason()
          : store.currentStepId() === 'sleeveAmounts' ? store.sleeveAmountsStepBlockedReason()
          : null;

      <div class="flex items-center justify-center flex-1 px-15">
        @if (footerBlocker) {
          <p class="px-15 py-8 text-sm text-center border rounded border-red-light bg-red-lightest text-red-dark"
            style="max-width: 46rem">{{ footerBlocker }}</p>
        }
      </div>

      <div class="flex items-center mx-10 my-0 gap-x-15">
        <!-- "No changes to submit" used to sit here as a paragraph beside the button. It is a
             submit blocker now, so it appears in the panel at the top of the step with every
             other reason, and the footer panel above covers it. One place, one kind of problem. -->
        @if (store.currentStep() < store.totalSteps) {
          <button type="button" class="button is-mint is-lg" [disabled]="!store.canProceed()"
            (click)="store.goNext()">Next</button>
        } @else {
          <button type="button" class="button is-mint is-lg flex justify-center"
            [disabled]="store.isSubmitting() || !store.canSubmit()" (click)="store.submit()">
            @if (store.isSubmitting()) {
              <span class="loader-sm is-white"></span>
            } @else {
              Submit
            }
          </button>
        }
      </div>
    </footer>
  }
</div>
